import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMarketing, EmailTemplate } from "../../../context/MarketingContext";
import { Input } from "../../../components/ui/input2";
import { Button } from "../../../components/ui/Button";
import { EmailTemplateDetailsDrawer } from "../../../components/marketing/EmailTemplateDetailsDrawer";
import { TemplateListSkeleton } from "../../../components/marketing/TemplateListSkeleton";
import { useToast } from "../../../hooks/useToast";
import { formatApiErrors } from "../../../services/marketingTemplatesApi";

const EmailTemplatesListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { emailTemplates, emailTemplatesLoading, removeEmailTemplate } = useMarketing();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(
    () =>
      emailTemplates.filter((t) => {
        const term = search.toLowerCase();
        return (
          t.name.toLowerCase().includes(term) ||
          (t.description || "").toLowerCase().includes(term)
        );
      }),
    [emailTemplates, search],
  );

  const handleRowClick = (template: EmailTemplate) => {
    setSelected(template);
    setDrawerOpen(true);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base lg:text-lg font-semibold text-gray-900">
              Email Templates
            </h2>
            <p className="text-xs lg:text-sm text-gray-500">
              Create reusable email content for your campaigns.
            </p>
          </div>
          <Button
            onClick={() => navigate("/marketing/email-templates/create")}
            className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Email Template
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="pl-9"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {emailTemplatesLoading ? (
            <TemplateListSkeleton />
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No email templates yet. Click{" "}
              <span className="font-semibold text-[#885ABB]">New Email Template</span>{" "}
              to create your first one.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleRowClick(template)}
                  className="w-full text-left px-4 lg:px-5 py-3 lg:py-3.5 hover:bg-purple-50/40 transition flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {template.name}
                    </p>
                    {template.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {template.description}
                      </p>
                    )}
                  </div>
                  <span className="text-[11px] font-semibold text-[#885ABB] uppercase tracking-wide">
                    View
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <EmailTemplateDetailsDrawer
        isOpen={drawerOpen}
        template={selected}
        onClose={() => setDrawerOpen(false)}
        onEdit={(id) => navigate(`/marketing/email-templates/${id}/edit`)}
        onDelete={async (id) => {
          try {
            await removeEmailTemplate(id);
            toast.success("Email template deleted.");
            setSelected(null);
          } catch (err) {
            toast.error(formatApiErrors(err));
            throw err;
          }
        }}
      />
    </div>
  );
};

export default EmailTemplatesListPage;

